/* KERNEL SNAPSHOT (screenplay-to-blocking port, 2026-07-06)
 * Source: TimelineWebApp/src/projects/startFromScreenplay.ts (partial extraction, lines ~110-163) @ "Previs Pro - Web Port" (no VCS; provenance = path+date+sha)
 * sha256(source): b89492f8ab8b323ce3c1f39497c5c05c9395b1d995ab9008509af3e61f6d0db2
 * Recorded in src/staging/KERNEL_PROVENANCE.ts - update it if this file is deliberately forked.
 * FORKED 2026-09-30 (CASTBLEED): extraction bounded to the character's own intro segment, colour guard for bare black/white — see KERNEL_PROVENANCE forks[]. */
// ── Character dossiers (heuristic) ───────────────────────────────────────────
// A character's first ALL-CAPS appearance in action is their intro: `NAME, age, [ethnicity,] a/an
// [adjectives] HEAD-NOUN…`. Parse a concise appearance phrase from that line + the next scene. See
// docs/character-dossiers.md. Implicit characters (no intro) are left blank for the AI fallback / edit.
// 20260930 (fork): "from that line + the next scene" is no longer true — the phrase comes ONLY from the
// character's own segment of the intro paragraph (bounded by the other roster names). The two-scene
// window let one character's ethnicity/gender bleed onto everyone introduced near them (Airship: ZED,
// DAX, JERIS and MAC all read "Black woman" off "A red and black IMP" + DAX's "WOMAN").

export interface ImportedDialogue { character: string; text: string; parenthetical: string }
export interface ImportedScene {
  slugline: string;
  number: string;
  actions: string[];
  dialogue: ImportedDialogue[];
}

const ETHNICITY_RE = /\b(african[- ]?americans?|black|white|caucasian|latinas?|latinos?|hispanic|asian|south[- ]?asian|east[- ]?asian|indian|native[- ]?american|middle[- ]?eastern|biracial|mixed[- ]?race|wasp)\b/i;
const GENDER_NOUN_RE = /\b(man|woman|boy|girl|guy|gentleman|lady|male|female|teenager|teen)\b/i;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normEthnicity(raw: string): string {
  const l = raw.toLowerCase();
  if (l === 'wasp') return 'white';
  if (l === 'black') return 'Black';
  if (l.startsWith('african')) return 'African-American';
  if (l === 'caucasian') return 'Caucasian';
  return raw;
}

// 20260930 (fork) — how far a segment may reach from the name on either side. Scripts describe a
// character either right after the name ("MARLA, 30s, a weary Black nurse") or just before it ("…a
// CYBORG WOMAN, with copper arms and legs. She goes by MAC."); a PDF import can merge several action
// paragraphs into one, so the paragraph alone is not a tight enough bound.
const SEGMENT_CAP = 220;

// 20260930 (fork) — a bare "black"/"white" is far more often a colour ("a red and black IMP", "her black
// cloak", "the TV flashes black to white") than an ethnicity. It counts as an ethnicity only when it
// qualifies a person noun (skipping at most one adjective: "Black man", "black teenage boy") or is
// written capitalised mid-sentence ("a weary Black nurse"). Every other ethnicity word stays unconditional.
const PERSON_NOUN_RE = /^(?:man|men|woman|women|boy|boys|girl|girls|guy|guys|gentleman|gentlemen|lady|ladies|male|female|teenager|teenagers|teen|teens|person|people|kid|kids|child|children|nurse|cop|cops|doctor|officer|detective|soldier|teacher|student|mother|father|mom|dad|son|daughter|wife|husband|family|couple|youth)$/i;

// 20260930 (review F5) — a person noun can itself be a noun modifier: "the black family car", "the black
// cop car", "a white student ID". The noun counts only when it ENDS the phrase: punctuation or end of text
// follows it, or one of these function words, or a verb form ("a Black man walks", "a black nurse stared").
const PERSON_PHRASE_FOLLOWER_RE = /^(?:with|in|who|and|at|on|by|of|from|wearing|holding)$/i;

function personNounEndsPhrase(rest: string): boolean {
  const w = /^\s*([A-Za-z'’-]+)/.exec(rest);
  if (!w) return true; // punctuation or end of text
  if (PERSON_PHRASE_FOLLOWER_RE.test(w[1])) return true;
  return /(?:s|ed|ing)$/i.test(w[1]) && !PERSON_NOUN_RE.test(w[1]);
}

function isEthnicColour(text: string, idx: number, word: string): boolean {
  // Up to two words after the colour word (one adjective may sit before the person noun).
  let p = idx + word.length;
  for (let i = 0; i < 2; i++) {
    const w = /^\s+([A-Za-z'’-]+)/.exec(text.slice(p));
    if (!w) break;
    p += w[0].length;
    if (PERSON_NOUN_RE.test(w[1]) && personNounEndsPhrase(text.slice(p))) return true;
  }
  if (!/^[BW][a-z]+$/.test(word)) return false; // lowercase, or an ALL-CAPS shout with no person noun
  const before = text.slice(0, idx).replace(/[\s"'“‘(]+$/, '');
  return before !== '' && !/[.!?]$/.test(before); // "Black." as a sentence opener is scene colour, not a person
}

/** First ethnicity word inside [from, to) of `text`, colour words guarded (see isEthnicColour). */
function scanEthnicity(text: string, from: number, to: number): string | undefined {
  const re = new RegExp(ETHNICITY_RE.source, 'gi');
  re.lastIndex = from;
  for (let m = re.exec(text); m && m.index + m[0].length <= to; m = re.exec(text)) {
    if (!/^(black|white)$/i.test(m[1]) || isEthnicColour(text, m.index, m[1])) return m[1];
  }
  return undefined;
}

/** Snap a cap position back / forward to a word boundary so a cut never leaves a half word ("man|kind"). */
function capEnd(text: string, p: number): number {
  if (p >= text.length) return text.length;
  while (p > 0 && !/\s/.test(text[p])) p--;
  return p;
}
function capStart(text: string, p: number): number {
  if (p <= 0) return 0;
  while (p < text.length && !/\s/.test(text[p - 1])) p++;
  return p;
}

/**
 * Parse an appearance phrase ("African-American man, 24") from the character's own intro segment.
 * 20260930 (fork): was a flat 180-char slice after a case-insensitive indexOf of the name, with
 * whole-window fallbacks for ethnicity and gender. The segment is now the intro paragraph cut at the
 * nearest OTHER roster name on each side (and SEGMENT_CAP from the name), and nothing outside it is
 * read. 20260930 (review F1): a neighbour's own sentence is dropped from the text before the name, the
 * age comes from the text after the name (or an age written right before it), and sentence-initial
 * pronouns count only from this name's own sentence once a neighbour precedes it.
 * KNOWN LIMIT (review F4): the segment is cut at roster names, not at descriptor phrases, so inside ONE
 * sentence that describes two characters BEFORE their names ("A lanky white boy, TOBY, and a weary Black
 * woman, MARLA, wait by the lift.") the earlier name reads the later one's descriptor from its after-text
 * (TOBY = "Black woman") and the later name, whose before-text is dropped as a same-sentence neighbour's,
 * gets nothing. The usual "NAME, age, a/an …" form is not affected. Cast picks stay user-editable.
 */
function parseAppearance(text: string, self: RegExpExecArray, otherNamesRe: RegExp | null): string {
  const selfStart = self.index, selfEnd = self.index + self[0].length;
  let segStart = 0, segEnd = text.length;
  if (otherNamesRe) {
    for (const m of text.matchAll(otherNamesRe)) {
      const at = m.index ?? 0;
      if (at + m[0].length <= selfStart) segStart = at + m[0].length;
      else if (at >= selfEnd) { segEnd = at; break; }
    }
  }
  // 20260930 (review F1): another roster name earlier in the paragraph owns its own sentence — "ZED, 50,
  // stands at the wheel. MAC enters." must not give MAC ZED's 50 (or a gender from it). The before-text
  // starts after the first sentence break that follows the neighbour; with no break at all (same sentence:
  // "TOBY, 20s, a lanky white boy, and MARLA, 30s, …") nothing before this name is read. What survives
  // the neighbour's sentence is kept: "…Next to him stands a CYBORG WOMAN. She goes by MAC." still
  // describes MAC. With no neighbour before it (segStart 0) the whole paragraph up to the name is kept.
  const bounded = segStart > 0;
  if (bounded) {
    const brk = /[.!?]["'”’)]*/.exec(text.slice(segStart, selfStart));
    segStart = brk ? segStart + brk.index + brk[0].length : selfStart;
  }
  segStart = Math.max(segStart, capStart(text, selfStart - SEGMENT_CAP));
  segEnd = Math.max(selfEnd, Math.min(segEnd, capEnd(text, selfEnd + SEGMENT_CAP)));
  // Start of this name's own sentence (never before segStart).
  let ownStart = segStart;
  for (const m of text.slice(segStart, selfStart).matchAll(/[.!?]["'”’)]*\s+/g)) ownStart = segStart + (m.index ?? 0) + m[0].length;
  const after = text.slice(selfEnd, segEnd);
  // 20260930 (review F3): "NAME, 12," / "NAME (12)" is the character's own intro, and it runs AFTER the
  // name, so the text before it belongs to someone else — "A MAN, 50s, enters. JO, 12, follows." must not
  // give JO "man" now that a generic roster word (MAN) no longer bounds the segment.
  const introAfter = /^\s*(?:,\s*|\(\s*)\d{2}(?!\d)/.test(after);
  const nounStart = introAfter ? selfStart : segStart;
  const before = text.slice(nounStart, selfStart);
  // The text after the name is read first (the "NAME, age, a/an …" form), then the text before it.
  const eth = scanEthnicity(text, selfEnd, segEnd) ?? scanEthnicity(text, nounStart, selfStart);
  let gender = GENDER_NOUN_RE.exec(after)?.[1] ?? GENDER_NOUN_RE.exec(before)?.[1];
  // Pronoun fallback for gender when no head-noun (e.g. "…WASP. He is…"). 20260930 (fork): only
  // SENTENCE-INITIAL pronouns inside the segment count, majority wins — "An ugly ORC helps her up."
  // names someone else, while "His body is skeletally skinny. His name is JERIS." describes JERIS.
  // 20260930 (review F1): once a neighbour precedes the name (or the name opens its own "NAME, age," intro)
  // pronouns count only from this name's own sentence — in "ZED, 50, stands at the wheel. He barks
  // orders. MAC enters." the "He" is still ZED.
  if (!gender) {
    const pronounStart = bounded || introAfter ? ownStart : segStart;
    let he = 0, she = 0;
    for (const m of text.matchAll(/(?:^|[.!?]["'”’)]*\s+)(He|His|Him|She|Her|Hers)\b/g)) {
      const at = (m.index ?? 0) + m[0].length - m[1].length;
      if (at < pronounStart || at >= segEnd) continue;
      if (m[1] === 'He' || m[1] === 'His' || m[1] === 'Him') he++; else she++;
    }
    if (he > she) gender = 'man'; else if (she > he) gender = 'woman';
  }
  // 20260930 (review F1): the age comes from the text after the name, as before the fork; from the text
  // before it only when it sits right against the name ("Black man, 40s, KEVIN jogs…"). "At 11:30 the door
  // bangs open. MAC enters." is not an 11-year-old MAC. A clock time ("11:30") is never an age.
  const ageRe = /(?:^|[^\d:])(\d{2})(?![\d:])/;
  const adjacentAgeRe = /(?:^|[^\d:])(\d{2})(?:['’]?s)?\s*\)?\s*,?\s*$/;
  const age = ageRe.exec(after)?.[1] ?? adjacentAgeRe.exec(before)?.[1];
  const lead = [eth ? normEthnicity(eth) : '', gender ? gender.toLowerCase() : ''].filter(Boolean).join(' ');
  return [lead, age].filter(Boolean).join(', ').trim();
}

/** name(upper) → appearance phrase, for characters with a parseable intro. */
export function buildDossiers(scenes: ImportedScene[], names: string[]): Map<string, string> {
  const flat: { scene: number; text: string }[] = [];
  scenes.forEach((s, i) => s.actions.forEach(t => flat.push({ scene: i, text: t })));
  // 20260930 (fork): every roster first name that can bound a segment (ALL-CAPS, ≥ 2 chars), longest
  // first so an alternation never stops on a shorter prefix.
  // 20260930 (review F3): a generic roster name (WOMAN, MAN, GIRL, OLD MAN, THE DRIVER…) is also the head
  // noun writers capitalise in a named character's intro ("a bizarre steampunk WOMAN with octopus arms…
  // This is DAX."), so it never bounds a segment — as a bound it cut DAX's own "woman" away. Such an entry
  // still parses its own intro through its `re` below.
  const tokens = [...new Set(names.map(n => n.split(/\s+/)[0].toUpperCase()).filter(t => t.length >= 2))]
    .filter(t => !PERSON_NOUN_RE.test(t) && GENDER_NOUN_RE.exec(t)?.[0].length !== t.length && !/^(?:THE|OLD|YOUNG)$/.test(t))
    .sort((a, b) => b.length - a.length);
  const out = new Map<string, string>();
  for (const name of names) {
    const first = name.split(/\s+/)[0];
    if (first.length < 2) continue;
    const own = first.toUpperCase();
    const re = new RegExp('\\b' + escapeRegExp(own) + '\\b'); // case-sensitive → ALL-CAPS only
    const introIdx = flat.findIndex(f => re.test(f.text));
    if (introIdx < 0) continue;
    const text = flat[introIdx].text;
    const self = re.exec(text);
    if (!self) continue;
    const others = tokens.filter(t => t !== own);
    const otherNamesRe = others.length > 0
      ? new RegExp('\\b(?:' + others.map(escapeRegExp).join('|') + ')\\b', 'g') // case-sensitive, like `re`
      : null;
    const appearance = parseAppearance(text, self, otherNamesRe);
    if (appearance) out.set(name.toUpperCase(), appearance);
  }
  return out;
}

/** A bounded context window for a gap character: ~½ page before their first appearance → +2 scenes.
 *  Keeps the AI-fallback payload small, fast, and focused (see docs/character-dossiers.md). */
export function buildWindow(scenes: ImportedScene[], name: string): string {
  const first = name.split(/\s+/)[0];
  const re = new RegExp('\\b' + escapeRegExp(first.toUpperCase()) + '\\b');
  let intro = scenes.findIndex(s => s.actions.some(a => re.test(a)));
  if (intro < 0) intro = scenes.findIndex(s => s.dialogue.some(d => d.character.toUpperCase() === name.toUpperCase()));
  if (intro < 0) intro = 0;
  const sceneText = (s: ImportedScene) =>
    `[${s.slugline}] ${s.actions.join(' ')} ${s.dialogue.map(d => `${d.character}: ${d.text}`).join(' ')}`
      .replace(/\s+/g, ' ').trim();
  const parts: string[] = [];
  if (intro > 0) parts.push('…' + sceneText(scenes[intro - 1]).slice(-280)); // ~½ page before
  for (let i = intro; i <= Math.min(scenes.length - 1, intro + 2); i++) parts.push(sceneText(scenes[i]));
  return parts.join('\n').slice(0, 1800);
}
